/**
 * PA item flags — the same file in care-provider and care-admin.
 *
 * One model for every actor: a flag is about ONE item of an encounter, with a
 * type and a note (GET/POST /v2/pa/{checkin}/item-flags, PATCH …/{flag_id}).
 * The author can edit the type and note while the flag is active; everyone
 * who works the encounter's items (Customer Support, Claims, the facility)
 * sees which item is flagged, the type and the note.
 *
 *   const api = createItemFlagsApi(apiRequest);           // apiRequest from features/notify
 *   const flagState = useEncounterItemFlags(api, checkinId);
 *   <ItemFlagTag flags={flagState.byItem[item.id_]} onClick={…} />
 *   <ItemFlagsModal … />   per item: list, edit, flag this item
 *   <ItemFlagsList … />    whole encounter (drawers, panels)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Modal, Select, Space, Spin, Tag, Typography, notification } from 'antd';
import { EditOutlined, FlagFilled, FlagOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

export const ITEM_FLAGS_CHANGED_EVENT = 'pa-item-flags-changed';
const MAX_NOTE = 1000;
const STATUS_COLOR = { active: 'red', resolved: 'green', dismissed: 'default' };

export function createItemFlagsApi(request) {
  const base = (checkinId) => `v2/pa/${encodeURIComponent(checkinId)}/item-flags`;
  return {
    list: (checkinId) => request('get', base(checkinId)),
    create: (checkinId, body) => request('post', base(checkinId), body),
    update: (checkinId, flagId, body) => request('patch', `${base(checkinId)}/${encodeURIComponent(flagId)}`, body),
  };
}

const isOk = (res) => res && (res.error === 0 || res.error === false || res.error === '0');

const formatWhen = (value) => {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

/** Loads every flag on the encounter's items; reloads on ITEM_FLAGS_CHANGED_EVENT. */
export function useEncounterItemFlags(api, checkinId) {
  const [flags, setFlags] = useState([]);
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    if (!checkinId) return;
    setLoading(true);
    try {
      const res = await api.list(checkinId);
      if (isOk(res)) {
        setFlags(Array.isArray(res.data?.flags) ? res.data.flags : []);
        setAllowedTypes(Array.isArray(res.data?.allowed_flag_types) ? res.data.allowed_flag_types : []);
        setError('');
      } else {
        setError(res?.message || 'Flags could not be loaded.');
      }
    } finally {
      setLoading(false);
    }
  }, [api, checkinId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const onChange = (e) => {
      if (!e?.detail?.checkinId || e.detail.checkinId === checkinId) reload();
    };
    window.addEventListener(ITEM_FLAGS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(ITEM_FLAGS_CHANGED_EVENT, onChange);
  }, [checkinId, reload]);

  const byItem = useMemo(() => {
    const map = {};
    flags.forEach((f) => {
      if (f.item_id == null) return;
      (map[f.item_id] = map[f.item_id] || []).push(f);
    });
    return map;
  }, [flags]);

  const activeCount = useMemo(() => flags.filter((f) => f.status === 'active').length, [flags]);

  return { flags, byItem, activeCount, allowedTypes, loading, error, reload };
}

export function announceItemFlagsChanged(checkinId) {
  window.dispatchEvent(new CustomEvent(ITEM_FLAGS_CHANGED_EVENT, { detail: { checkinId } }));
}

/** Clear mark on a flagged item row: "Flagged · Service Not Rendered (+1)". Renders nothing when no active flag. */
export function ItemFlagTag({ flags = [], onClick }) {
  const active = flags.filter((f) => f.status === 'active');
  if (!active.length) return null;
  const extra = active.length > 1 ? ` (+${active.length - 1})` : '';
  return (
    <Tag
      color="red"
      icon={<FlagFilled />}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default', marginInlineEnd: 0, whiteSpace: 'normal' }}
      title={active.map((f) => `${f.flag_label}: ${f.note}`).join('\n')}
    >
      Flagged · {active[0].flag_label}{extra}
    </Tag>
  );
}

export function ItemFlagCard({ flag, showItem = false, onEdit, extra = null }) {
  const by = [flag.created_by_label, flag.created_by_name].filter(Boolean).join(': ');
  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderLeft: `4px solid ${flag.status === 'active' ? '#ff4d4f' : '#d9d9d9'}`,
        borderRadius: 8,
        padding: '10px 12px',
        background: flag.status === 'active' ? '#fff8f8' : '#fafafa',
      }}
    >
      {showItem ? (
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {flag.item_name || (flag.item_id ? `Item #${flag.item_id}` : 'Whole encounter (no item chosen)')}
        </div>
      ) : null}
      <Space size={6} wrap style={{ marginBottom: 6 }}>
        <Tag color={STATUS_COLOR[flag.status] || 'default'}>{String(flag.status || 'active').toUpperCase()}</Tag>
        <Tag color="volcano">{flag.flag_label || flag.flag_type}</Tag>
        {flag.edit_count ? <Text type="secondary" style={{ fontSize: 12 }}>edited {formatWhen(flag.edited_at)}</Text> : null}
      </Space>
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {flag.note ? flag.note : <Text type="secondary" italic>No note</Text>}
      </div>
      {flag.resolution_notes ? (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>Resolution: {flag.resolution_notes}</Text>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>
          {by ? `${by} · ` : ''}{formatWhen(flag.created_at)}
        </Text>
        {flag.can_edit && onEdit ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(flag)}>Edit</Button>
        ) : null}
        {extra}
      </div>
    </div>
  );
}

export function ItemFlagsList({ flags = [], showItem = false, onEdit, renderExtra, emptyText = 'No flags' }) {
  if (!flags.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      {flags.map((f) => (
        <ItemFlagCard key={f.id} flag={f} showItem={showItem} onEdit={onEdit} extra={renderExtra ? renderExtra(f) : null} />
      ))}
    </Space>
  );
}

/**
 * Create or edit form. Create: choose the item(s) unless `fixedItem` is given.
 * Edit: type and note of `initial`.
 */
export function FlagFormModal({ open, mode = 'create', allowedTypes = [], items = [], fixedItem = null, initial = null, submitting = false, onSubmit, onCancel }) {
  const [itemIds, setItemIds] = useState([]);
  const [type, setType] = useState();
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setItemIds(fixedItem ? [fixedItem.item_id] : []);
    setType(initial?.flag_type || undefined);
    setNote(initial?.note || '');
  }, [open, fixedItem, initial]);

  const needsItem = mode === 'create' && !fixedItem;
  const ready = Boolean(type) && note.trim() !== '' && (!needsItem || itemIds.length > 0);
  const typeOptions = allowedTypes.map((t) => (typeof t === 'string' ? { value: t, label: t.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) } : t));

  return (
    <Modal
      title={mode === 'edit' ? 'Edit flag' : fixedItem ? `Flag: ${fixedItem.item_name}` : 'Flag an item'}
      open={open}
      onCancel={onCancel}
      onOk={() => onSubmit({ item_ids: itemIds, flag_type: type, flag_reason: note.trim() })}
      okText={mode === 'edit' ? 'Save changes' : 'Submit flag'}
      okButtonProps={{ disabled: !ready }}
      confirmLoading={submitting}
      destroyOnClose
      width={560}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {needsItem ? (
          <div>
            <Text strong>Item *</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Choose the item you are flagging"
              value={itemIds}
              onChange={setItemIds}
              options={items.map((it) => ({ value: it.item_id, label: it.item_name }))}
              optionFilterProp="label"
            />
          </div>
        ) : null}
        <div>
          <Text strong>Flag type *</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Select a flag type"
            value={type}
            onChange={setType}
            options={typeOptions}
          />
        </div>
        <div>
          <Text strong>Note *</Text>
          <TextArea
            rows={4}
            style={{ marginTop: 8 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What is wrong with this item?"
            maxLength={MAX_NOTE}
            showCount
          />
        </div>
      </Space>
    </Modal>
  );
}

/**
 * Raise or edit flags through the API; reports errors; announces the change.
 * Returns { creating, editing, submitting, openCreate, openEdit, modal } where `modal` is the element to render.
 */
export function useFlagEditor({ api, checkinId, allowedTypes, items = [], onChanged }) {
  const [state, setState] = useState({ open: false, mode: 'create', fixedItem: null, initial: null });
  const [submitting, setSubmitting] = useState(false);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  const openCreate = useCallback((fixedItem = null) => setState({ open: true, mode: 'create', fixedItem, initial: null }), []);
  const openEdit = useCallback((flag) => setState({ open: true, mode: 'edit', fixedItem: null, initial: flag }), []);

  const submit = useCallback(async (values) => {
    setSubmitting(true);
    try {
      const res = state.mode === 'edit'
        ? await api.update(checkinId, state.initial.flag_id, { flag_type: values.flag_type, flag_reason: values.flag_reason })
        : await api.create(checkinId, { item_ids: values.item_ids, flag_type: values.flag_type, flag_reason: values.flag_reason });
      if (!isOk(res)) {
        notification.error({ message: state.mode === 'edit' ? 'Flag not saved' : 'Flag not submitted', description: res?.message || 'Please try again.' });
        return;
      }
      notification.success({ message: state.mode === 'edit' ? 'Flag updated' : 'Flag submitted' });
      close();
      announceItemFlagsChanged(checkinId);
      if (onChanged) onChanged();
    } finally {
      setSubmitting(false);
    }
  }, [api, checkinId, state, close, onChanged]);

  const modal = (
    <FlagFormModal
      open={state.open}
      mode={state.mode}
      allowedTypes={allowedTypes}
      items={items}
      fixedItem={state.fixedItem}
      initial={state.initial}
      submitting={submitting}
      onSubmit={submit}
      onCancel={close}
    />
  );
  return { openCreate, openEdit, modal };
}

/** Every flag on one item, with "Flag this item" and edit for the author. */
export function ItemFlagsModal({ open, onClose, item, flags = [], canFlag = false, onFlag, onEdit, loading = false }) {
  return (
    <Modal
      title={<span><FlagOutlined /> Flags · {item?.item_name || 'Item'}</span>}
      open={open}
      onCancel={onClose}
      footer={[
        canFlag ? <Button key="flag" icon={<FlagOutlined />} onClick={onFlag}>Flag this item</Button> : null,
        <Button key="close" type="primary" onClick={onClose}>Close</Button>,
      ].filter(Boolean)}
      width={600}
    >
      {loading ? <Spin /> : <ItemFlagsList flags={flags} onEdit={onEdit} emptyText="This item has not been flagged" />}
    </Modal>
  );
}
